import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Injectable, Injector, makeStateKey } from '@angular/core';

import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { AuthGuard } from '../auth/auth.guard';
import { HotToastService } from '@ngxpert/hot-toast';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  busy$ = new BehaviorSubject(false);
  isAdmin = false; // To be set from outside, bypass cache when true

  private cache = new Map<string, any>();
  get busy() {
    return this.busy$.getValue();
  }
  set busy(b) {
    this.busy$.next(b);
  }
  constructor(
    private http: HttpClient,
    private injector: Injector,
    private router: Router,
  ) {}

  get<TData, TOptions = any>(
    url: string,
    getOptions?: TOptions,
    useMemoryCache = false,
  ): Observable<TData> {
    const options: {
      observe: 'body';
      responseType: 'json';
      params: HttpParams;
      headers: Record<string, string>;
    } = {
      params: this.anyToParams(getOptions || {}, this.isAdmin),
      observe: 'body' as const,
      responseType: 'json' as const,
      headers: (getOptions as any)?.headers,
    };
    const cacheKey = makeStateKey(`${url}|${JSON.stringify(options)}`);
    if (useMemoryCache && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }
    const observable = this.http
      .get<TData>(`${this.getUrl(url)}`, options)
      .pipe(
        tap((res) => this.sessionFilter(res)),
        catchError((err) => this.sessionCatch(err) as Observable<TData>),
        shareReplay(1),
      );
    if (useMemoryCache) {
      this.cache.set(cacheKey, observable);
    }
    return observable;
  }

  delete<T>(url: string) {
    return this.http.delete<T>(this.getUrl(url)).pipe(
      tap((res) => this.sessionFilter(res)),
      catchError((err) => this.sessionCatch(err) as Observable<T>),
    );
  }

  post<TResult = any, TBody = any>(
    url: string,
    body: TBody = {} as any,
    options: Record<string, any> = {},
  ) {
    return this.http
      .post<TResult>(this.getUrl(url), body, {
        ...options,
        // withCredentials: true,
      })
      .pipe(
        tap((res) => this.sessionFilter(res)),
        catchError((err) => this.sessionCatch(err) as Observable<TResult>),
      );
  }

  put<TResult = any, TBody = any>(
    url: string,
    body: Partial<TBody> = {},
    options: Record<string, any> = {},
  ) {
    return this.http
      .put<TResult>(this.getUrl(url), body, {
        ...options,
        // withCredentials: true,
      })
      .pipe(
        tap((res) => this.sessionFilter(res)),
        catchError((err) => this.sessionCatch(err) as Observable<TResult>),
      );
  }

  patch<TResult = any, TBody = any>(
    url: string,
    body: Partial<TBody> = {},
    options: Record<string, any> = {},
  ) {
    return this.http
      .patch<TResult>(this.getUrl(url), body, {
        ...options,
        // withCredentials: true,
      })
      .pipe(
        tap((res) => this.sessionFilter(res)),
        catchError((err) => this.sessionCatch(err) as Observable<TResult>),
      );
  }

  /**
   * Get full URL
   */
  getUrl(url: string) {
    if (/^http/.test(url)) {
      return url;
    }

    // return `${
    //   /localhost/i.test(location.href)
    //     ? `${environment.apiBase}${environment.apiRoot}`
    //     : `${environment.apiRoot}`
    // }${url.replace(/^\//, '')}`;

    return environment.apiBase + environment.apiRoot + url.replace(/^\//, '');
  }

  /**
   * Reduce any nested Object-like structure to HttpParams, so that nested Arrays and Objects are JSON-encoded
   */
  private anyToParams(obj: any, bustCache = false): HttpParams {
    return Object.entries(
      Object.assign(obj || {}, bustCache ? { r: new Date().getTime() } : {}),
    ).reduce((params, [key, value]) => {
      return params.set(
        key,
        typeof value === 'object' && value !== null
          ? JSON.stringify(value)
          : String(value),
      );
    }, new HttpParams());
  }

  private sessionFilter(resp: any) {
    const code = resp?.code;
    if (code === 440) {
      const auth = this.injector.get<AuthGuard>(AuthGuard);
      auth.cancelPageAndLogout();
    } else if (code === 423) {
      window.dispatchEvent(
        new CustomEvent('security:check', {
          detail: { provider: resp.provider },
        }),
      );
    } else if (code === 501) {
      // Unimplemented
      this.router.navigate(['/demo/unavailable']);
    } /* else if (code === 403) {
      const toast = this.injector.get<HotToastService>(HotToastService);
      toast.error(
        `Action failed: ${
          resp?.message || 'You do not have permission to perform this action'
        }`
      );
    }*/
  }

  // TODO: fix this
  private sessionCatch(err: HttpErrorResponse): any {
    if (err.status === 440) {
      const auth = this.injector.get<AuthGuard>(AuthGuard);
      auth.cancelPageAndLogout();
      return EMPTY;
    } else if (err.status === 423) {
      // i can't get this to fire for now, maybe this isn't even doing anything
      window.dispatchEvent(
        new CustomEvent('security:check', { detail: { provider: 'unknown' } }),
      );
      return EMPTY;
    } else if (err.status === 501) {
      // Unimplemented
      this.router.navigate(['/demo/unavailable']);
      return EMPTY;
    } else if (err.status === 403) {
      const toast = this.injector.get<HotToastService>(HotToastService);
      toast.error(
        "Request failed: Unauthorized. Is your system's clock out of sync?",
      );
      return EMPTY;
    }

    console.log('Server request error:', err);

    console.log(err);
    if (err.error instanceof Blob) {
      return throwError(() => new Error(this.blobToString(err.error)));
    }

    return throwError(
      () => new Error(`Request failed: [${err.name}] ${err.statusText}`),
    );
  }

  private blobToString(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    const xmlRequest = new XMLHttpRequest();
    xmlRequest.open('GET', url, false);
    xmlRequest.send();
    URL.revokeObjectURL(url);
    return xmlRequest.responseText;
  }
}
